import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, LogOut, User, Bell, BellOff, Crown, ChevronRight, Mail, Loader2, CalendarDays, Sparkles, Lock, Globe, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

// App name constant
const APP_NAME = "the soft ritual";

// Common timezones for dropdown
const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
];

export default function Profile({ auth }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [name, setName] = useState(auth.user?.name || "");
  const [saving, setSaving] = useState(false);
  const [boundaryScripts, setBoundaryScripts] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(auth.user?.profile_photo || null);
  
  // Notification state (browser)
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [reminderTime, setReminderTime] = useState('09:00');
  const [savingReminder, setSavingReminder] = useState(false);
  
  // Email reminder state
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(false);
  const [emailReminderTime, setEmailReminderTime] = useState('09:00');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Blog notification state
  const [blogNotificationsEnabled, setBlogNotificationsEnabled] = useState(true);
  
  // Timezone state
  const [userTimezone, setUserTimezone] = useState('America/New_York');
  
  // Weekly summary state
  const [weeklySummaryEnabled, setWeeklySummaryEnabled] = useState(false);
  const [weeklySummaryDay, setWeeklySummaryDay] = useState('sunday');
  const [weeklySummaryTime, setWeeklySummaryTime] = useState('10:00');
  const [sendingWeeklySummary, setSendingWeeklySummary] = useState(false);

  // Tone preference state
  const [tonePreference, setTonePreference] = useState('reflective'); // 'practical' or 'reflective'

  // Affirmation settings state
  const [affirmationsEnabled, setAffirmationsEnabled] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchBoundaryScripts();
    checkNotificationStatus();
    loadReminderPreferences();
    loadWeeklySummaryPreferences();
    loadTonePreference();
    loadAffirmationSettings();
    checkPremiumStatus();
    
    // Auto-detect timezone on first load
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detectedTimezone) {
      setUserTimezone(detectedTimezone);
    }
  }, []);

  const loadTonePreference = async () => {
    try {
      const response = await fetch(`${API}/profile/tone`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTonePreference(data.tone || 'reflective');
      }
    } catch (e) {
      // Use localStorage as fallback
      const saved = localStorage.getItem('tonePreference');
      if (saved) setTonePreference(saved);
    }
  };

  const saveTonePreference = async (tone) => {
    setTonePreference(tone);
    localStorage.setItem('tonePreference', tone);
    try {
      await fetch(`${API}/profile/tone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tone })
      });
      toast.success(tone === 'practical' ? "Responses will be more practical" : "Responses will be more reflective");
    } catch (e) {
      console.error('Failed to save tone preference:', e);
    }
  };

  const checkPremiumStatus = async () => {
    try {
      const response = await fetch(`${API}/subscription/status`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setIsPremium(data.plan !== 'free');
      }
    } catch (e) {
      console.error('Failed to check premium status:', e);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await fetch(`${API}/subscription/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ return_url: window.location.href })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const errorData = await response.json();
        // Show specific error message from server
        const errorMessage = errorData.detail || "Unable to open subscription management";
        toast.error(errorMessage);
      }
    } catch (e) {
      console.error('Failed to open customer portal:', e);
      if (e.message === 'Failed to fetch' || e.name === 'TypeError') {
        toast.error("Unable to connect. Please check your internet connection.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/profile/photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePhoto(data.profile_photo);
        // Update auth context
        if (auth.user) {
          auth.user.profile_photo = data.profile_photo;
        }
        toast.success("Profile photo updated");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload photo");
      }
    } catch (e) {
      console.error('Failed to upload photo:', e);
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      const response = await fetch(`${API}/profile/photo`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setProfilePhoto(null);
        if (auth.user) {
          auth.user.profile_photo = null;
        }
        toast.success("Profile photo removed");
      } else {
        toast.error("Failed to remove photo");
      }
    } catch (e) {
      console.error('Failed to remove photo:', e);
      toast.error("Failed to remove photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const loadAffirmationSettings = async () => {
    try {
      const response = await fetch(`${API}/affirmations/settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAffirmationsEnabled(data.affirmations_enabled);
      }
    } catch (e) {
      console.error('Failed to load affirmation settings:', e);
    }
  };

  const handleAffirmationToggle = async (enabled) => {
    setAffirmationsEnabled(enabled);
    try {
      await fetch(`${API}/affirmations/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ affirmations_enabled: enabled })
      });
      toast.success(enabled ? "Affirmations enabled" : "Affirmations disabled");
    } catch (e) {
      console.error('Failed to save affirmation settings:', e);
    }
  };

  const checkNotificationStatus = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      const savedEnabled = localStorage.getItem('notificationsEnabled') === 'true';
      setNotificationsEnabled(savedEnabled && Notification.permission === 'granted');
    }
  };

  const loadReminderPreferences = async () => {
    // Load browser notification time from localStorage
    const savedTime = localStorage.getItem('reminderTime');
    if (savedTime) {
      setReminderTime(savedTime);
    }
    
    // Load email preferences from backend
    try {
      const response = await fetch(`${API}/reminder-preferences`, { credentials: 'include' });
      if (response.ok) {
        const prefs = await response.json();
        setEmailRemindersEnabled(prefs.email_enabled || false);
        setEmailReminderTime(prefs.email_time || '09:00');
        // Load saved timezone, or keep auto-detected one
        if (prefs.timezone) {
          setUserTimezone(prefs.timezone);
        }
        // Load blog notification preference (default to true)
        setBlogNotificationsEnabled(prefs.blog_notifications !== false);
      }
    } catch (e) {
      console.error('Failed to load reminder preferences:', e);
    }
  };

  const saveBlogNotificationPreference = async (enabled) => {
    try {
      await fetch(`${API}/users/me/email-preferences?blog_notifications=${enabled}`, {
        method: 'PUT',
        credentials: 'include'
      });
      setBlogNotificationsEnabled(enabled);
      toast.success(enabled ? "Blog notifications enabled" : "Blog notifications disabled");
    } catch (e) {
      console.error('Failed to save blog notification preference:', e);
      toast.error("Failed to update preference");
    }
  };

  const saveEmailPreferences = async (enabled, time, tz = userTimezone) => {
    try {
      await fetch(`${API}/reminder-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          browser_enabled: notificationsEnabled,
          browser_time: reminderTime,
          email_enabled: enabled,
          email_time: time,
          timezone: tz
        })
      });
    } catch (e) {
      console.error('Failed to save email preferences:', e);
    }
  };

  const handleTimezoneChange = async (e) => {
    const newTimezone = e.target.value;
    setUserTimezone(newTimezone);
    await saveEmailPreferences(emailRemindersEnabled, emailReminderTime, newTimezone);
    toast.success("Timezone updated");
  };

  const handleEmailReminderToggle = async (enabled) => {
    setEmailRemindersEnabled(enabled);
    await saveEmailPreferences(enabled, emailReminderTime);
    toast.success(enabled ? "Email reminders enabled" : "Email reminders disabled");
  };

  const handleEmailTimeChange = async (e) => {
    const newTime = e.target.value;
    setEmailReminderTime(newTime);
    if (emailRemindersEnabled) {
      await saveEmailPreferences(true, newTime);
      toast.success("Email reminder time updated");
    }
  };

  const sendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API}/reminders/send-email`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }
      
      toast.success("Test email sent! Check your inbox.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Weekly Summary Functions
  const loadWeeklySummaryPreferences = async () => {
    try {
      const response = await fetch(`${API}/weekly-summary/preferences`, { credentials: 'include' });
      if (response.ok) {
        const prefs = await response.json();
        setWeeklySummaryEnabled(prefs.enabled || false);
        setWeeklySummaryDay(prefs.day_of_week || 'sunday');
        setWeeklySummaryTime(prefs.email_time || '10:00');
      }
    } catch (e) {
      console.error('Failed to load weekly summary preferences:', e);
    }
  };

  const saveWeeklySummaryPreferences = async (enabled, day, time) => {
    try {
      await fetch(`${API}/weekly-summary/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: enabled,
          day_of_week: day,
          email_time: time
        })
      });
    } catch (e) {
      console.error('Failed to save weekly summary preferences:', e);
    }
  };

  const handleWeeklySummaryToggle = async (enabled) => {
    setWeeklySummaryEnabled(enabled);
    await saveWeeklySummaryPreferences(enabled, weeklySummaryDay, weeklySummaryTime);
    toast.success(enabled ? "Weekly summary enabled" : "Weekly summary disabled");
  };

  const handleWeeklySummaryDayChange = async (e) => {
    const newDay = e.target.value;
    setWeeklySummaryDay(newDay);
    if (weeklySummaryEnabled) {
      await saveWeeklySummaryPreferences(true, newDay, weeklySummaryTime);
      toast.success("Summary day updated");
    }
  };

  const handleWeeklySummaryTimeChange = async (e) => {
    const newTime = e.target.value;
    setWeeklySummaryTime(newTime);
    if (weeklySummaryEnabled) {
      await saveWeeklySummaryPreferences(true, weeklySummaryDay, newTime);
      toast.success("Summary time updated");
    }
  };

  const sendTestWeeklySummary = async () => {
    setSendingWeeklySummary(true);
    try {
      const response = await fetch(`${API}/weekly-summary/send`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send summary');
      }
      
      toast.success("Weekly summary sent! Check your inbox.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingWeeklySummary(false);
    }
  };

  const handleNotificationToggle = async (enabled) => {
    if (enabled) {
      // Request permission
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        if (permission !== 'granted') {
          toast.error("Please allow notifications in your browser settings");
          return;
        }
      } else if (Notification.permission === 'denied') {
        toast.error("Notifications are blocked. Please enable them in browser settings.");
        return;
      }
      
      // Register service worker
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register('/sw.js');
        } catch (e) {
          console.error('SW registration failed:', e);
        }
      }
      
      setNotificationsEnabled(true);
      localStorage.setItem('notificationsEnabled', 'true');
      scheduleReminder(reminderTime);
      toast.success("Reminders enabled");
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('notificationsEnabled', 'false');
      cancelScheduledReminder();
      toast.success("Reminders disabled");
    }
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setReminderTime(newTime);
    localStorage.setItem('reminderTime', newTime);
    
    if (notificationsEnabled) {
      cancelScheduledReminder();
      scheduleReminder(newTime);
      toast.success("Reminder time updated");
    }
  };

  const scheduleReminder = (time) => {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    
    let scheduledTime = new Date();
    scheduledTime.setHours(hours, minutes, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }
    
    const delay = scheduledTime - now;
    
    const timeoutId = setTimeout(() => {
      showReminderNotification();
      scheduleReminder(time); // Reschedule for next day
    }, delay);
    
    localStorage.setItem('reminderTimeoutId', timeoutId.toString());
  };

  const cancelScheduledReminder = () => {
    const timeoutId = localStorage.getItem('reminderTimeoutId');
    if (timeoutId) {
      clearTimeout(Number(timeoutId));
      localStorage.removeItem('reminderTimeoutId');
    }
  };

  const showReminderNotification = async () => {
    if (Notification.permission !== 'granted') return;
    
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      registration.showNotification('The Soft Ritual', {
        body: 'Would you like to check in today?',
        icon: '/logo192.png',
        tag: 'soft-ritual-reminder',
        requireInteraction: false,
        data: { url: '/check-in' }
      });
    } else {
      new Notification('The Soft Ritual', {
        body: 'Would you like to check in today?',
        icon: '/logo192.png'
      });
    }
  };

  const testNotification = async () => {
    if (Notification.permission !== 'granted') {
      toast.error("Please enable notifications first");
      return;
    }
    await showReminderNotification();
    toast.success("Test notification sent");
  };

  const fetchBoundaryScripts = async () => {
    try {
      const response = await fetch(`${API}/boundary-scripts`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBoundaryScripts(data);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        toast.success("Profile updated");
        auth.checkAuth();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error("Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await auth.logout();
    navigate('/');
  };

  const copyScript = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="profile-page">
      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/dashboard">
            <Button variant="ghost" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <span 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Your profile
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-[#F5F2EE] rounded-full mb-8">
          <button
            onClick={() => setActiveTab("profile")}
            className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all ${
              activeTab === "profile"
                ? "bg-white text-[#2D2A26] shadow-sm"
                : "text-[#4A4744] hover:text-[#2D2A26]"
            }`}
            data-testid="tab-profile"
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab("notifications")}
            className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "notifications"
                ? "bg-white text-[#2D2A26] shadow-sm"
                : "text-[#4A4744] hover:text-[#2D2A26]"
            }`}
            data-testid="tab-notifications"
          >
            <Bell className="w-4 h-4" />
            Notifications
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
        <div className="animate-fade-in space-y-8">
          {/* Avatar with Photo Upload */}
          <div className="text-center">
            <div className="relative inline-block">
              {profilePhoto ? (
                <img 
                  src={profilePhoto.startsWith('http') ? profilePhoto : `${API.replace('/api', '')}${profilePhoto}`}
                  alt={auth.user?.name || 'Profile'}
                  className="w-24 h-24 rounded-full mx-auto border-4 border-white shadow-md object-cover"
                />
              ) : auth.user?.picture ? (
                <img 
                  src={auth.user.picture} 
                  alt={auth.user.name}
                  className="w-24 h-24 rounded-full mx-auto border-4 border-white shadow-md object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto">
                  <User className="w-12 h-12 text-[#A87C6D]" />
                </div>
              )}
              
              {/* Upload/Remove overlay */}
              <div className="absolute bottom-0 right-0 flex gap-1">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="w-8 h-8 rounded-full bg-[#A87C6D] text-white flex items-center justify-center shadow-md hover:bg-[#956d5f] transition-colors disabled:opacity-50"
                  title="Upload photo"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
                {profilePhoto && (
                  <button
                    onClick={handleRemovePhoto}
                    disabled={uploadingPhoto}
                    className="w-8 h-8 rounded-full bg-[#E6E2DD] text-[#4A4744] flex items-center justify-center shadow-md hover:bg-[#d9d5d0] transition-colors disabled:opacity-50"
                    title="Remove photo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            <p className="text-[#4A4744] font-light mt-4">{auth.user?.email}</p>
          </div>

          {/* Subscription Card */}
          <div className="paper-card rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-[#A87C6D]" />
                </div>
                <div>
                  <h3 
                    className="text-lg font-normal text-[#2D2A26]"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    Subscription
                  </h3>
                  <p className="text-sm text-[#4A4744] font-light">
                    {isPremium ? 'Inner Circle' : 'Open'}
                  </p>
                </div>
              </div>
            </div>
            
            {isPremium ? (
              <Button
                onClick={openCustomerPortal}
                disabled={portalLoading}
                variant="outline"
                className="w-full rounded-full border-[#A87C6D] text-[#A87C6D] hover:bg-[#A87C6D] hover:text-white"
                data-testid="manage-subscription-btn"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Opening...
                  </>
                ) : (
                  "Manage Subscription"
                )}
              </Button>
            ) : (
              <Link to="/subscription" className="block">
                <Button
                  className="w-full rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                >
                  Upgrade to Inner Circle
                </Button>
              </Link>
            )}
          </div>

          {/* Profile Form */}
          <div className="paper-card rounded-3xl p-6 md:p-8">
            <h2 
              className="text-xl font-normal text-[#2D2A26] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Personal details
            </h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#4A4744] font-light">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-xl py-5 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
                  data-testid="name-input"
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving || name === auth.user?.name}
                className="rounded-full px-6 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white disabled:opacity-50"
                data-testid="save-profile-btn"
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>

          {/* Experience Settings */}
          <div className="paper-card rounded-3xl p-6 md:p-8">
            <h2 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Your experience
            </h2>
            <p className="text-[#4A4744] font-light text-sm mb-6">
              Adjust how The Soft Ritual responds to you.
            </p>
            
            <div className="space-y-4">
              <Label className="text-[#4A4744] font-light text-sm">Response tone</Label>
              <div className="flex gap-3">
                <button
                  onClick={() => saveTonePreference('practical')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    tonePreference === 'practical' 
                      ? 'border-[#A87C6D] bg-[#A87C6D]/5' 
                      : 'border-[#E6E2DD] hover:border-[#A87C6D]/50'
                  }`}
                  data-testid="tone-practical"
                >
                  <p className={`font-medium text-sm ${tonePreference === 'practical' ? 'text-[#A87C6D]' : 'text-[#2D2A26]'}`}>
                    More practical
                  </p>
                  <p className="text-xs text-[#6E6A66] mt-1">
                    Direct, grounded, action-oriented
                  </p>
                </button>
                <button
                  onClick={() => saveTonePreference('reflective')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    tonePreference === 'reflective' 
                      ? 'border-[#A87C6D] bg-[#A87C6D]/5' 
                      : 'border-[#E6E2DD] hover:border-[#A87C6D]/50'
                  }`}
                  data-testid="tone-reflective"
                >
                  <p className={`font-medium text-sm ${tonePreference === 'reflective' ? 'text-[#A87C6D]' : 'text-[#2D2A26]'}`}>
                    More reflective
                  </p>
                  <p className="text-xs text-[#6E6A66] mt-1">
                    Spacious, gentle, feeling-focused
                  </p>
                </button>
              </div>
            </div>

            {/* Affirmations Section */}
            <div className="mt-6 pt-6 border-t border-[#E6E2DD]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className={`w-5 h-5 ${isPremium && affirmationsEnabled ? 'text-[#A87C6D]' : 'text-[#6E6A66]'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#2D2A26]">Custom affirmations</span>
                      {!isPremium && (
                        <span className="px-2 py-0.5 rounded-full bg-[#A87C6D]/10 text-[#A87C6D] text-xs">
                          Inner Circle
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#6E6A66]">
                      {isPremium 
                        ? "Receive personalized affirmations after check-ins" 
                        : "Unlock personalized affirmations with Inner Circle"
                      }
                    </p>
                  </div>
                </div>
                {isPremium ? (
                  <Switch
                    checked={affirmationsEnabled}
                    onCheckedChange={handleAffirmationToggle}
                    data-testid="affirmations-toggle"
                  />
                ) : (
                  <Lock className="w-5 h-5 text-[#6E6A66]" />
                )}
              </div>
              
              {/* Link to saved affirmations */}
              <Link to="/affirmations" className="block mt-4">
                <div className="flex items-center justify-between p-3 rounded-xl bg-[#F5F2ED] hover:bg-[#E6E2DD] transition-colors">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#A87C6D]" />
                    <span className="text-sm text-[#4A4744]">
                      {isPremium ? "View saved affirmations" : "Learn about affirmations"}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#6E6A66]" />
                </div>
              </Link>
            </div>
          </div>

          {/* Logout Button */}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign out
          </Button>
        </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
        <div className="animate-fade-in space-y-8">
          {/* Reminder Settings */}
          <div className="paper-card rounded-3xl p-6 md:p-8">
            <h2 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Gentle reminders
            </h2>
            <p className="text-[#4A4744] font-light text-sm mb-6">
              A soft nudge to check in with yourself. No pressure, just a gentle invitation.
            </p>
            
            <div className="space-y-6">
              {/* Browser Notifications */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {notificationsEnabled ? (
                      <Bell className="w-5 h-5 text-[#A87C6D]" />
                    ) : (
                      <BellOff className="w-5 h-5 text-[#6E6A66]" />
                    )}
                    <div>
                      <span className="text-[#2D2A26]">Browser notifications</span>
                      <p className="text-xs text-[#6E6A66]">Get notified in your browser</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationsEnabled}
                    onCheckedChange={handleNotificationToggle}
                    data-testid="notification-toggle"
                  />
                </div>
                
                {notificationsEnabled && (
                  <div className="space-y-3 pl-8 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#4A4744] font-light text-sm">Time</Label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={handleTimeChange}
                        className="px-3 py-2 rounded-lg border border-[#E6E2DD] bg-white text-[#2D2A26] focus:border-[#A87C6D] focus:outline-none text-sm"
                        data-testid="reminder-time-input"
                      />
                    </div>
                    <Button
                      onClick={testNotification}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs border-[#E6E2DD] text-[#4A4744] hover:bg-[#F5F2ED]"
                      data-testid="test-notification-btn"
                    >
                      Send test notification
                    </Button>
                  </div>
                )}
                
                {notificationPermission === 'denied' && (
                  <p className="text-sm text-[#C8553D] pl-8">
                    Notifications are blocked. Please enable them in browser settings.
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[#E6E2DD]"></div>

              {/* Timezone Setting */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#A87C6D]" />
                    <div>
                      <span className="text-[#2D2A26]">Your timezone</span>
                      <p className="text-xs text-[#6E6A66]">For reminder scheduling</p>
                    </div>
                  </div>
                </div>
                <div className="pl-8">
                  <select
                    value={userTimezone}
                    onChange={handleTimezoneChange}
                    className="w-full px-3 py-2 rounded-lg border border-[#E6E2DD] bg-white text-[#2D2A26] focus:border-[#A87C6D] focus:outline-none text-sm"
                    data-testid="timezone-select"
                  >
                    {COMMON_TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                    {/* If user's detected timezone isn't in common list, add it */}
                    {!COMMON_TIMEZONES.find(tz => tz.value === userTimezone) && (
                      <option value={userTimezone}>{userTimezone}</option>
                    )}
                  </select>
                  <p className="text-xs text-[#6E6A66] mt-1">
                    Auto-detected from your device
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-[#E6E2DD]"></div>

              {/* Email Reminders */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${emailRemindersEnabled ? 'text-[#A87C6D]' : 'text-[#6E6A66]'}`} />
                    <div>
                      <span className="text-[#2D2A26]">Email reminders</span>
                      <p className="text-xs text-[#6E6A66]">Receive a gentle email each day</p>
                    </div>
                  </div>
                  <Switch
                    checked={emailRemindersEnabled}
                    onCheckedChange={handleEmailReminderToggle}
                    data-testid="email-reminder-toggle"
                  />
                </div>
                
                {emailRemindersEnabled && (
                  <div className="space-y-3 pl-8 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#4A4744] font-light text-sm">Time</Label>
                      <input
                        type="time"
                        value={emailReminderTime}
                        onChange={handleEmailTimeChange}
                        className="px-3 py-2 rounded-lg border border-[#E6E2DD] bg-white text-[#2D2A26] focus:border-[#A87C6D] focus:outline-none text-sm"
                        data-testid="email-time-input"
                      />
                    </div>
                    <p className="text-xs text-[#6E6A66]">
                      Reminders will be sent at this time in your timezone
                    </p>
                    <Button
                      onClick={sendTestEmail}
                      disabled={sendingTestEmail}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs border-[#E6E2DD] text-[#4A4744] hover:bg-[#F5F2ED]"
                      data-testid="test-email-btn"
                    >
                      {sendingTestEmail ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        'Send test email'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[#E6E2DD]"></div>

              {/* Blog Notifications */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${blogNotificationsEnabled ? 'text-[#A87C6D]' : 'text-[#6E6A66]'}`} />
                    <div>
                      <span className="text-[#2D2A26]">Blog notifications</span>
                      <p className="text-xs text-[#6E6A66]">Get notified when new articles are published in The Ritual Edit</p>
                    </div>
                  </div>
                  <Switch
                    checked={blogNotificationsEnabled}
                    onCheckedChange={(checked) => saveBlogNotificationPreference(checked)}
                    data-testid="blog-notification-toggle"
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-[#E6E2DD]"></div>

              {/* Weekly Summary */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarDays className={`w-5 h-5 ${weeklySummaryEnabled ? 'text-[#A87C6D]' : 'text-[#6E6A66]'}`} />
                    <div>
                      <span className="text-[#2D2A26]">Weekly reflection summary</span>
                      <p className="text-xs text-[#6E6A66]">A gentle look at your emotional patterns</p>
                    </div>
                  </div>
                  <Switch
                    checked={weeklySummaryEnabled}
                    onCheckedChange={handleWeeklySummaryToggle}
                    data-testid="weekly-summary-toggle"
                  />
                </div>
                
                {weeklySummaryEnabled && (
                  <div className="space-y-3 pl-8 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#4A4744] font-light text-sm">Day</Label>
                      <select
                        value={weeklySummaryDay}
                        onChange={handleWeeklySummaryDayChange}
                        className="px-3 py-2 rounded-lg border border-[#E6E2DD] bg-white text-[#2D2A26] focus:border-[#A87C6D] focus:outline-none text-sm"
                        data-testid="weekly-summary-day"
                      >
                        <option value="sunday">Sunday</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-[#4A4744] font-light text-sm">Time (UTC)</Label>
                      <input
                        type="time"
                        value={weeklySummaryTime}
                        onChange={handleWeeklySummaryTimeChange}
                        className="px-3 py-2 rounded-lg border border-[#E6E2DD] bg-white text-[#2D2A26] focus:border-[#A87C6D] focus:outline-none text-sm"
                        data-testid="weekly-summary-time"
                      />
                    </div>
                    <Button
                      onClick={sendTestWeeklySummary}
                      disabled={sendingWeeklySummary}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs border-[#E6E2DD] text-[#4A4744] hover:bg-[#F5F2ED]"
                      data-testid="test-weekly-summary-btn"
                    >
                      {sendingWeeklySummary ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        'Send test summary'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Emotional Patterns */}
          {auth.user?.emotional_patterns?.length > 0 && (
            <div className="paper-card rounded-3xl p-6 md:p-8">
              <h2 
                className="text-xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                What you've been holding
              </h2>
              <p className="text-[#4A4744] font-light text-sm mb-4">
                These are themes that have come up in your reflections. They're not diagnoses — just gentle observations.
              </p>
              <div className="flex flex-wrap gap-2">
                {auth.user.emotional_patterns.map((pattern, idx) => (
                  <span 
                    key={idx}
                    className="px-4 py-2 rounded-full bg-[#E6E2DD] text-[#4A4744] text-sm"
                  >
                    {pattern}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Boundary Scripts */}
          <div className="paper-card rounded-3xl p-6 md:p-8">
            <h2 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Boundary scripts
            </h2>
            <p className="text-[#4A4744] font-light text-sm mb-4">
              Gentle phrases you can use when you need to set a boundary. Tap to copy.
            </p>
            <div className="space-y-2">
              {boundaryScripts.map((script) => (
                <button
                  key={script.id}
                  onClick={() => copyScript(script.script)}
                  className="w-full text-left p-4 rounded-xl bg-[#F5F2ED] hover:bg-[#E6E2DD] transition-colors text-[#2D2A26] font-light"
                  data-testid={`script-${script.id}`}
                >
                  "{script.script}"
                </button>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Disclaimer */}
        <div className="text-center pt-8 mt-8 border-t border-[#E6E2DD]">
          <p className="text-sm text-[#6E6A66]">
            The Soft Ritual is not therapy or medical advice.<br />
            If you're in crisis, please reach out to trusted support.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
